import { useCallback } from 'react'
import { collection, serverTimestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { addDoc } from '../lib/firestore'
import { uploadFeedbackAttachments } from '../lib/feedbackAttachments'
import type { Feedback } from '../types'

export type FeedbackInput = Pick<
  Feedback,
  'category' | 'message' | 'immediateReview' | 'errorCode' | 'errorEventId' | 'errorMessage' | 'logs'
>

/**
 * Uploads any attachments, then writes a feedback doc stamped with the current
 * route and user agent. Resolves only on the server ack, which on festival
 * signal can take minutes — callers shouldn't block the UI on it. Attachments
 * that fail to upload are dropped (and counted) rather than losing the report.
 */
export function useSubmitFeedback(userId: string | null) {
  return useCallback(
    async (input: FeedbackInput, attachments: Blob[] = []): Promise<{ failedAttachments: number }> => {
      if (!userId) return { failedAttachments: 0 }
      // Captured before the uploads, which can outlast a navigation.
      const route = (window.location.pathname + window.location.search).slice(0, 500)
      const { urls, failed } = attachments.length
        ? await uploadFeedbackAttachments(userId, attachments)
        : { urls: [], failed: 0 }
      const optional = Object.fromEntries(Object.entries(input).filter(([, v]) => v !== undefined && v !== false))
      await addDoc(collection(db, 'feedback'), {
        userId,
        ...optional,
        ...(urls.length ? { attachmentUrls: urls } : {}),
        route,
        userAgent: navigator.userAgent.slice(0, 1000),
        createdAt: serverTimestamp(),
      })
      return { failedAttachments: failed }
    },
    [userId],
  )
}
